import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema } from '../components/StructuredData';
import { promotionsPageSchema } from '../components/StructuredData';
import './Features.css';

const Promotions: React.FC = () => {
  return (
    <div className="feature-page">
      <SEO
        title="Guest Promotions - TableChat | Welcome Promos, Daily Specials & Timed Offers"
        description="Deliver welcome promotions, daily specials, and time-based offers directly to guests the moment they sit down. No app download, no QR scan — just tap and engage. TableChat automates restaurant promotions through NFC-powered table-side messaging."
        canonical="https://tablechat.io/features/promotions"
      />
      <StructuredData data={promotionsPageSchema} />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'Features', url: 'https://tablechat.io/features/promotions' },
        { name: 'Guest Promotions', url: 'https://tablechat.io/features/promotions' },
      ])} />

      {/* Hero */}
      <section className="feature-hero feature-hero-split">
        <div className="container">
          <div className="feature-hero-grid">
            <div className="feature-hero-text">
              <h1>Turn Every Table Into a Marketing Moment</h1>
              <p className="hero-lead">
                Most restaurant promotions never reach the guest. Printed table tents go unread. Social media posts 
                miss the people already in your seats. TableChat delivers promotions directly to guests the second 
                they sit down — through the same NFC chat they use to communicate with staff.
              </p>
            </div>
            <div className="feature-hero-image">
              <img 
                src="/chalkboard-specials.png"
                alt="Restaurant daily specials chalkboard showing food and drink items — TableChat delivers these digitally to guests at the table"
                className="feature-hero-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Problem-Solution Blocks */}
      <section className="feature-problem-solution">
        <div className="container">
          <div className="section-intro">
            <h2>Promotions That Reach Guests at the Right Moment</h2>
            <p>
              TableChat's automated promotion engine delivers your marketing messages when they have the highest 
              impact — while the guest is seated, engaged, and ready to order.
            </p>
          </div>

          <div className="feature-blocks">
            {/* Welcome Promotions */}
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>Welcome Promotions</h3>
                <p>
                  Every guest who taps an NFC tag receives a personalized welcome message from your restaurant. 
                  Attach a first-visit offer, a seasonal greeting, or a loyalty signup link — all delivered 
                  automatically before the guest sends their first message.
                </p>
                <p>
                  Unlike printed collateral that gets ignored or QR codes that require a separate scan, 
                  welcome promos appear inside the conversation the guest is already using. Open rates 
                  are effectively 100% because the message is part of the chat experience.
                </p>
              </div>
              <div className="feature-visual">
                <h4>What You Can Deliver</h4>
                <ul>
                  <li>First-visit discount or free appetizer offer</li>
                  <li>Seasonal welcome message with your restaurant's voice</li>
                  <li>Loyalty program signup link with one-tap enrollment</li>
                  <li>Event announcements for the current week</li>
                  <li>Custom messaging per restaurant location</li>
                </ul>
              </div>
            </div>

            {/* Daily Specials */}
            <div className="feature-block reverse">
              <div className="feature-block-content">
                <h3>Daily Specials Chalkboard</h3>
                <p>
                  Update your daily specials once from the management console. Every guest who sits down sees 
                  them automatically — formatted clearly, delivered instantly, and displayed directly in their 
                  chat window. No printing, no table tent swaps, no hoping the server remembers to mention them.
                </p>
                <p>
                  The digital chalkboard supports multiple items with descriptions and pricing. Update specials 
                  at any time during the day and new guests immediately see the latest version.
                </p>
              </div>
              <div className="feature-visual">
                <h4>How Daily Specials Work</h4>
                <ul>
                  <li>Set specials from the management console in seconds</li>
                  <li>Guests see specials automatically when they tap in</li>
                  <li>Update mid-service — new guests get the latest version</li>
                  <li>Include item names, descriptions, and pricing</li>
                  <li>Runs alongside welcome promos without overlap</li>
                </ul>
              </div>
            </div>

            {/* Time-Based Offers */}
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>Time-Based Offers</h3>
                <p>
                  Run happy hour specials that automatically start and stop on schedule. Promote brunch-only 
                  items during weekend mornings. Push late-night menu options after 9 PM. TableChat's 
                  time-based promotion system lets you target the right offer to the right daypart without 
                  manual intervention.
                </p>
                <p>
                  Combined with welcome promos and daily specials, time-based offers give you a complete 
                  promotional toolkit that works passively — set it up once and let it run, or adjust 
                  on the fly from your management console.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Time-Based Capabilities</h4>
                <ul>
                  <li>Schedule offers by day of week and time of day</li>
                  <li>Automatic start/stop — no manual toggling</li>
                  <li>Stack with daily specials and welcome promos</li>
                  <li>Ideal for happy hour, brunch, late-night menus</li>
                  <li>Adjust schedules from the management console</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="feature-stats">
        <div className="container">
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-number">100%</div>
              <p className="stat-label">Guest visibility — promos appear inside the active chat</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">0</div>
              <p className="stat-label">Printed materials needed — everything is digital</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">&lt;30s</div>
              <p className="stat-label">Time to update specials from the management console</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="feature-how-it-works">
        <div className="container">
          <h2>How TableChat Promotions Work</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h4>Configure Once</h4>
              <p>Set your welcome message, daily specials, and timed offers from the management console. Choose what to show and when.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h4>Guest Taps In</h4>
              <p>When a guest taps the NFC tag at their table, they enter the chat and immediately see your promotions before sending any message.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h4>Measure & Adjust</h4>
              <p>Track engagement through daily operational reports. Adjust specials and offers in real time based on what's moving.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="feature-cta">
        <div className="container">
          <h2>Stop Printing. Start Connecting.</h2>
          <p>Give every guest a reason to order more — automatically, every time they sit down.</p>
          <Link to="/signup" className="btn btn-primary btn-large">Get Started Today</Link>
        </div>
      </section>
    </div>
  );
};

export default Promotions;
